import axios, { AxiosInstance, AxiosResponse } from 'axios';
import config from '../config';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Si el body es FormData, eliminar Content-Type para que axios lo setee automáticamente
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;

        // Only redirect to login if it's a 401 and not already on auth endpoints
        if (error.response?.status === 401 && !original._retry) {
          const isAuthEndpoint = original.url?.includes('/auth/');

          // Don't retry or redirect for auth endpoints
          if (isAuthEndpoint) {
            return Promise.reject(error);
          }

          original._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken();
              localStorage.setItem('authToken', response.data.token);
              return this.api(original);
            } else {
              // Only redirect if we're not already on login page
              if (!window.location.pathname.includes('/login')) {
                localStorage.removeItem('authToken');
                localStorage.removeItem('refreshToken');
                window.location.href = '/login';
              }
            }
          } catch (refreshError) {
            // Only redirect if we're not already on login page
            if (!window.location.pathname.includes('/login')) {
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  auth = {
    // El acceso es por nombre de usuario; el backend acepta el correo como alternativa.
    login: async (username: string, password: string): Promise<AxiosResponse> =>
      this.api.post('/auth/login', { username, password }),

    register: async (userData: any): Promise<AxiosResponse> =>
      this.api.post('/auth/register', userData),

    logout: async (): Promise<AxiosResponse> =>
      this.api.post('/auth/logout'),

    profile: async (): Promise<AxiosResponse> =>
      this.api.get('/auth/profile'),

    changePassword: async (
      currentPassword: string,
      newPassword: string,
      confirmPassword: string
    ): Promise<AxiosResponse> =>
      this.api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),

    refreshToken: async (): Promise<AxiosResponse> =>
      this.api.post('/auth/refresh-token'),
  };

  admin = {
    dashboard: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/dashboard'),

    getUsers: async (params?: { status?: string; role?: string }): Promise<AxiosResponse> =>
      this.api.get('/admin/users', { params }),

    approveUser: async (userId: number): Promise<AxiosResponse> =>
      this.api.post(`/admin/users/${userId}/approve`),

    rejectUser: async (userId: number): Promise<AxiosResponse> =>
      this.api.post(`/admin/users/${userId}/reject`),

    getMentors: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/mentors'),

    getMentees: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/mentees'),

    createRelationship: async (data: {
      mentor_id: number;
      mentee_id: number;
      start_date?: string;
      relationship_goals?: string;
    }): Promise<AxiosResponse> =>
      this.api.post('/admin/relationships', data),

    getRelationships: async (params?: { status?: string }): Promise<AxiosResponse> =>
      this.api.get('/admin/relationships', { params }),

    updateRelationship: async (relationshipId: number, data: any): Promise<AxiosResponse> =>
      this.api.put(`/admin/relationships/${relationshipId}`, data),

    endRelationship: async (
      relationshipId: number,
      data: {
        reason?: string;
        mentee_rating?: number;
        mentor_rating?: number;
      }
    ): Promise<AxiosResponse> =>
      this.api.post(`/admin/relationships/${relationshipId}/end`, data),

    getRelationshipDetails: async (relationshipId: number): Promise<AxiosResponse> =>
      this.api.get(`/admin/relationships/${relationshipId}`),

    getStats: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/stats'),

    // Program Stages
    getStages: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/stages'),

    // Reports and Analytics
    getAnalytics: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/reports/analytics'),

    getParticipationReport: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/reports/participation'),
  };

  mentor = {
    dashboard: async (): Promise<AxiosResponse> =>
      this.api.get('/mentor/dashboard'),

    getMentees: async (): Promise<AxiosResponse> =>
      this.api.get('/mentor/mentees'),

    getSessions: async (): Promise<AxiosResponse> =>
      this.api.get('/mentor/sessions'),

    createSession: async (sessionData: any): Promise<AxiosResponse> =>
      this.api.post('/mentor/sessions', sessionData),

    updateSession: async (sessionId: number, sessionData: any): Promise<AxiosResponse> =>
      this.api.put(`/mentor/sessions/${sessionId}`, sessionData),

    getMessages: async (): Promise<AxiosResponse> =>
      this.api.get('/mentor/messages'),

    sendMessage: async (messageData: any): Promise<AxiosResponse> =>
      this.api.post('/mentor/messages', messageData),

    getProfile: async (): Promise<AxiosResponse> =>
      this.api.get('/mentor/profile'),

    updateProfile: async (profileData: any): Promise<AxiosResponse> =>
      this.api.put('/mentor/profile', profileData),

    getStats: async (): Promise<AxiosResponse> =>
      this.api.get('/mentor/stats'),
  };

  mentee = {
    dashboard: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/dashboard'),

    getMentor: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/mentor'),

    getSessions: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/sessions'),

    // Goal management endpoints
    getGoals: async (params?: { status?: string; category?: string; priority?: string }): Promise<AxiosResponse> =>
      this.api.get('/mentee/goals', { params }),

    getGoal: async (goalId: number): Promise<AxiosResponse> =>
      this.api.get(`/mentee/goals/${goalId}`),

    createGoal: async (goalData: any): Promise<AxiosResponse> =>
      this.api.post('/mentee/goals', goalData),

    updateGoal: async (goalId: number, goalData: any): Promise<AxiosResponse> =>
      this.api.put(`/mentee/goals/${goalId}`, goalData),

    deleteGoal: async (goalId: number): Promise<AxiosResponse> =>
      this.api.delete(`/mentee/goals/${goalId}`),

    addGoalUpdate: async (goalId: number, updateData: { update_text: string }): Promise<AxiosResponse> =>
      this.api.post(`/mentee/goals/${goalId}/update`, updateData),

    updateGoalStatus: async (goalId: number, status: string): Promise<AxiosResponse> =>
      this.api.put(`/mentee/goals/${goalId}/status`, { status }),

    getGoalStats: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/goals/stats'),

    getMessages: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/messages'),

    sendMessage: async (messageData: any): Promise<AxiosResponse> =>
      this.api.post('/mentee/messages', messageData),

    getProfile: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/profile'),

    updateProfile: async (profileData: any): Promise<AxiosResponse> =>
      this.api.put('/mentee/profile', profileData),


    getProgress: async (): Promise<AxiosResponse> =>
      this.api.get('/mentee/progress'),
  };

  common = {
    getExpertiseAreas: async (): Promise<AxiosResponse> =>
      this.api.get('/expertise-areas'),

    getExpertiseCategories: async (): Promise<AxiosResponse> =>
      this.api.get('/expertise-areas/categories'),

    getExperienceLevels: async (): Promise<AxiosResponse> =>
      this.api.get('/experience-levels'),
  };

  notifications = {
    getAll: async (params?: any): Promise<AxiosResponse> =>
      this.api.get('/notifications', { params }),

    getCount: async (): Promise<AxiosResponse> =>
      this.api.get('/notifications/count'),

    getById: async (id: number): Promise<AxiosResponse> =>
      this.api.get(`/notifications/${id}`),

    markAsRead: async (id: number): Promise<AxiosResponse> =>
      this.api.put(`/notifications/${id}/read`),

    markMultipleAsRead: async (ids: number[]): Promise<AxiosResponse> =>
      this.api.put('/notifications/read-multiple', { notification_ids: ids }),

    markAllAsRead: async (): Promise<AxiosResponse> =>
      this.api.put('/notifications/read-all'),

    delete: async (id: number): Promise<AxiosResponse> =>
      this.api.delete(`/notifications/${id}`),

    createTest: async (data: any): Promise<AxiosResponse> =>
      this.api.post('/notifications/test', data),

    announce: async (data: any): Promise<AxiosResponse> =>
      this.api.post('/notifications/announce', data),
  };

  messages = {
    getConversations: async (): Promise<AxiosResponse> =>
      this.api.get('/messages/conversations'),
    getThread: async (channel: 'dupla' | 'support'): Promise<AxiosResponse> =>
      this.api.get(`/messages/thread/${channel}`),
    send: async (data: { channel: 'dupla' | 'support'; message: string }): Promise<AxiosResponse> =>
      this.api.post('/messages/send', data),
    markRead: async (channel: 'dupla' | 'support'): Promise<AxiosResponse> =>
      this.api.put(`/messages/read/${channel}`),
    remove: async (id: number): Promise<AxiosResponse> =>
      this.api.delete(`/messages/${id}`),
    // Admin
    supportInbox: async (): Promise<AxiosResponse> =>
      this.api.get('/messages/support/inbox'),
    supportThread: async (userId: number): Promise<AxiosResponse> =>
      this.api.get(`/messages/support/thread/${userId}`),
    supportReply: async (data: { user_id: number; message: string }): Promise<AxiosResponse> =>
      this.api.post('/messages/support/reply', data),
    announce: async (data: { title: string; message: string }): Promise<AxiosResponse> =>
      this.api.post('/messages/announce', data),
  };

  evaluations = {
    getAll: async (params?: any): Promise<AxiosResponse> =>
      this.api.get('/evaluations', { params }),

    getPending: async (): Promise<AxiosResponse> =>
      this.api.get('/evaluations/pending'),

    getStats: async (): Promise<AxiosResponse> =>
      this.api.get('/evaluations/stats'),

    getById: async (id: number): Promise<AxiosResponse> =>
      this.api.get(`/evaluations/${id}`),

    getMenteeEvaluations: async (menteeId: number): Promise<AxiosResponse> =>
      this.api.get(`/evaluations/mentee/${menteeId}`),

    create: async (data: any): Promise<AxiosResponse> =>
      this.api.post('/evaluations', data),

    update: async (id: number, data: any): Promise<AxiosResponse> =>
      this.api.put(`/evaluations/${id}`, data),

    delete: async (id: number): Promise<AxiosResponse> =>
      this.api.delete(`/evaluations/${id}`),
  };

  settings = {
    getAll: async (): Promise<AxiosResponse> =>
      this.api.get('/settings'),

    getByCategory: async (category: string): Promise<AxiosResponse> =>
      this.api.get(`/settings/category/${category}`),

    getByKey: async (key: string): Promise<AxiosResponse> =>
      this.api.get(`/settings/${key}`),

    update: async (key: string, value: any): Promise<AxiosResponse> =>
      this.api.put(`/settings/${key}`, { value }),

    updateBatch: async (settings: Record<string, any>): Promise<AxiosResponse> =>
      this.api.put('/settings/batch', settings),

    reset: async (): Promise<AxiosResponse> =>
      this.api.post('/settings/reset'),

    export: async (): Promise<AxiosResponse> =>
      this.api.get('/settings/export'),

    import: async (data: any): Promise<AxiosResponse> =>
      this.api.post('/settings/import', data),
  };

  notificationTemplates = {
    getAll: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/notification-templates'),

    update: async (key: string, data: { subject: string; body: string; cta: string }): Promise<AxiosResponse> =>
      this.api.put(`/admin/notification-templates/${key}`, data),

    reset: async (key: string): Promise<AxiosResponse> =>
      this.api.delete(`/admin/notification-templates/${key}`),
  };

  invitations = {
    preview: async (): Promise<AxiosResponse> =>
      this.api.get('/admin/invitations'),

    sendTest: async (email?: string): Promise<AxiosResponse> =>
      this.api.post('/admin/invitations/test', email ? { email } : {}),

    sendAll: async (onlyPending = false): Promise<AxiosResponse> =>
      this.api.post('/admin/invitations/send', { confirm: true, only_pending: onlyPending }),
  };

  audit = {
    getAll: async (params?: any): Promise<AxiosResponse> =>
      this.api.get('/audit', { params }),

    getById: async (id: number): Promise<AxiosResponse> =>
      this.api.get(`/audit/${id}`),

    getStats: async (params?: { days?: number }): Promise<AxiosResponse> =>
      this.api.get('/audit/stats', { params }),

    getActions: async (): Promise<AxiosResponse> =>
      this.api.get('/audit/actions'),

    getTables: async (): Promise<AxiosResponse> =>
      this.api.get('/audit/tables'),

    export: async (params?: any): Promise<AxiosResponse> =>
      this.api.get('/audit/export', { params }),

    cleanup: async (days: number = 90): Promise<AxiosResponse> =>
      this.api.delete('/audit/cleanup', { params: { days } }),
  };

  // Generic HTTP methods for direct API calls
  get = async (url: string, config?: any): Promise<AxiosResponse> =>
    this.api.get(url, config);

  post = async (url: string, data?: any, config?: any): Promise<AxiosResponse> =>
    this.api.post(url, data, config);

  put = async (url: string, data?: any, config?: any): Promise<AxiosResponse> =>
    this.api.put(url, data, config);

  delete = async (url: string, config?: any): Promise<AxiosResponse> =>
    this.api.delete(url, config);

  patch = async (url: string, data?: any, config?: any): Promise<AxiosResponse> =>
    this.api.patch(url, data, config);

  private refreshToken = async (): Promise<AxiosResponse> =>
    this.api.post('/auth/refresh-token');
}

const apiService = new ApiService();
export { apiService };
export default apiService;